// keyrd_console/src/components/PilotSystemStatus.js
import React, { useEffect, useState } from "react";
import { getPilotSystemStatus } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const panelStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const panelTitleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const kvGridStyle = {
  display: "grid",
  gridTemplateColumns: "200px 1fr",
  gap: 10,
  alignItems: "start",
};

const keyStyle = {
  color: "#475569",
  fontWeight: 600,
  fontSize: 14,
};

const valueStyle = {
  color: "#0f172a",
  fontSize: 14,
  wordBreak: "break-word",
};

const warningStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fdba74",
  fontSize: 13,
  fontWeight: 600,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

function KV({ label, value }) {
  return (
    <div style={kvGridStyle}>
      <div style={keyStyle}>{label}</div>
      <div style={valueStyle}>{value || "—"}</div>
    </div>
  );
}

function PilotSystemStatus({ auth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotSystemStatus(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot system status.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot system status…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Governance</h1>
          <div style={subtitleStyle}>
            Truth page for release, registry, protocol, stage mode, and safety posture.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={warningStyle}>
        This page is the fastest way to verify pilot safety posture. It must reflect real or explicitly stubbed governance truth.
      </div>

      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>System State</h2>
        <div style={rowWrapStyle}>
          <PilotStatusPill
            status={data.pilot_mode || auth?.pilotMode || "unknown"}
            label={`Mode: ${data.pilot_mode || auth?.pilotMode || "unknown"}`}
          />
          <PilotStatusPill
            status={(data.shadow_only_mode ?? true) ? "true" : "false"}
            label={(data.shadow_only_mode ?? true) ? "Shadow-Only" : "Shadow Disabled"}
          />
          <PilotStatusPill
            status={(data.rl_updates_blocked ?? true) ? "true" : "false"}
            label={(data.rl_updates_blocked ?? true) ? "RL Updates Blocked" : "RL Updates Enabled"}
          />
          <PilotStatusPill
            status={(data.live_issuance_enabled ?? false) ? "true" : "false"}
            label={(data.live_issuance_enabled ?? false) ? "Live Issuance Disabled" : "Live Issuance Enabled"}
          />
          <PilotStatusPill
            status={(data.system_pause ?? false) ? "paused" : "active"}
            label={(data.system_pause ?? false) ? "System Paused" : "System Running"}
          />
        </div>
      </div>

      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>Governance Metadata</h2>
        <KV label="Active Release ID" value={data.active_release_id || auth?.activeReleaseId} />
        <KV label="Protocol Version" value={data.protocol_version || auth?.protocolVersion} />
        <KV label="Registry Version" value={data.registry_version || auth?.registryVersion} />
        <KV label="Registry Hash" value={data.registry_hash || auth?.registryHash} />
        <KV label="Last Updated UTC" value={data.last_updated_utc} />
        <KV label="Mismatch Warning" value={data.mismatch_warning} />
      </div>

      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>Runtime Flags / Safety Indicators</h2>
        <KV label="System Pause" value={String(data.system_pause ?? false)} />
        <KV label="Shadow-Only Mode" value={String(data.shadow_only_mode ?? true)} />
        <KV label="RL Updates Blocked" value={String(data.rl_updates_blocked ?? true)} />
        <KV label="Live Issuance Enabled" value={String(data.live_issuance_enabled ?? false)} />
        <KV label="Scheduler State" value={data.scheduler_state} />
        <KV label="Reward Worker State" value={data.reward_worker_state} />
      </div>
    </div>
  );
}

export default PilotSystemStatus;